/**
 * Upload local files into a collection: drag-and-drop or a file chooser,
 * validated client-side (type allow-list + 50 MB cap) before anything is sent,
 * then uploaded one request per file so each has its own progress and error.
 */

import { useRef, useState, type DragEvent } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconCloudUpload, IconUpload, IconX } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { uploadIndexFile, type AddDocumentsOut } from "@/shared/api/indexes";
import { UPLOAD_EXTENSIONS, formatBytes, validateUpload } from "./collectionMeta";

interface Props {
  indexId: string;
  onAdded: (result: AddDocumentsOut) => void;
}

type ItemState = "ready" | "invalid" | "uploading" | "done" | "error";

interface Item {
  key: string;
  file: File;
  state: ItemState;
  progress: number;
  message: string | null;
}

const STATE_COLOR: Record<ItemState, string> = {
  ready: "gray",
  invalid: "red",
  uploading: "blue",
  done: "green",
  error: "red",
};

export function UploadSourceTab({ indexId, onAdded }: Props) {
  const getToken = useAccessToken();
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const addFiles = (list: FileList | File[]) => {
    const next: Item[] = Array.from(list).map((file, i) => {
      const problem = validateUpload(file);
      return {
        key: `${file.name}-${file.size}-${Date.now()}-${i}`,
        file,
        state: problem ? "invalid" : "ready",
        progress: 0,
        message: problem,
      };
    });
    setItems((prev) => [...prev, ...next]);
  };

  const patch = (key: string, change: Partial<Item>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...change } : it)));

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  const uploadAll = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      for (const item of items.filter((i) => i.state === "ready" || i.state === "error")) {
        patch(item.key, { state: "uploading", progress: 0, message: null });
        try {
          const result = await uploadIndexFile(token, indexId, item.file, (p) =>
            patch(item.key, { progress: Math.round(p * 100) }),
          );
          patch(item.key, {
            state: "done",
            progress: 100,
            message: result.skipped_duplicates > 0 ? "Already in the collection" : null,
          });
          onAdded(result);
        } catch (err) {
          patch(item.key, {
            state: "error",
            message: err instanceof ApiError ? err.message : String(err),
          });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const pending = items.filter((i) => i.state === "ready" || i.state === "error").length;

  return (
    <Stack gap="sm">
      <Paper
        withBorder
        radius="md"
        p="xl"
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          borderStyle: "dashed",
          cursor: "pointer",
          background: dragging ? "var(--mantine-color-blue-light)" : undefined,
        }}
      >
        <Stack align="center" gap={4}>
          <ThemeIcon variant="light" size={40} radius="xl">
            <IconCloudUpload size={22} />
          </ThemeIcon>
          <Text fw={500}>Drop files here, or click to choose</Text>
          <Text size="xs" c="dimmed">
            PDF, Word, Excel, PowerPoint, CSV or text — up to 50 MB each
          </Text>
        </Stack>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          aria-label="Choose files to upload"
          accept={UPLOAD_EXTENSIONS.join(",")}
          onChange={(e) => {
            if (e.currentTarget.files) addFiles(e.currentTarget.files);
            e.currentTarget.value = "";
          }}
        />
      </Paper>

      {items.length > 0 && (
        <Stack gap={6}>
          {items.map((it) => (
            <Paper key={it.key} withBorder radius="sm" p="xs">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" truncate>
                      {it.file.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatBytes(it.file.size)}
                    </Text>
                    <Badge size="xs" variant="light" color={STATE_COLOR[it.state]}>
                      {it.state === "invalid" ? "rejected" : it.state}
                    </Badge>
                  </Group>
                  {(it.state === "uploading" || it.state === "done") && (
                    <Progress
                      value={it.progress}
                      size="sm"
                      aria-label={`Upload progress for ${it.file.name}`}
                    />
                  )}
                  {it.message && (
                    <Text size="xs" c={it.state === "done" ? "dimmed" : "red"}>
                      {it.message}
                    </Text>
                  )}
                </Stack>
                {it.state !== "uploading" && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`Remove ${it.file.name} from the list`}
                    onClick={() => setItems((prev) => prev.filter((p) => p.key !== it.key))}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )}
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      <Group justify="flex-end">
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={() => void uploadAll()}
          loading={busy}
          disabled={pending === 0}
        >
          Upload {pending} file{pending === 1 ? "" : "s"}
        </Button>
      </Group>
    </Stack>
  );
}
